// Types for the inventory system

export interface InventoryDomain {
  id: string;
  code: string;
  name: string;
  section: InventorySection;
  description: string | null;
  display_order: number;
}

export type InventorySection = 
  | 'leadership_behaviour'
  | 'leadership_pressure'
  | 'motivation_drivers'
  | 'values_assumptions'
  | 'contribution_orientation'
  | 'decision_governance'
  | 'fairness_working'
  | 'team_climate';

export interface InventoryQuestion {
  id: string;
  domain_id: string;
  question_text: string;
  subscale: string | null;
  reverse_scored: boolean;
  display_order: number;
  is_active: boolean;
  domain?: InventoryDomain;
}

export interface InventorySession {
  id: string;
  user_id: string | null;
  email: string | null;
  name: string | null;
  role: string | null;
  practice_name: string | null;
  session_type: 'individual' | 'partner_selection';
  current_question_index: number;
  total_questions: number;
  started_at: string;
  completed_at: string | null;
}

export interface InventoryResponse {
  id: string;
  session_id: string;
  question_id: string;
  response_value: number;
}

export interface InventoryResult {
  id: string;
  session_id: string;
  domain_scores: Record<string, number>;
  primary_archetype: PartnerArchetype | null;
  secondary_archetype: PartnerArchetype | null;
  pressure_archetype: PartnerArchetype | null;
  top_strengths: string[];
  risk_zones: string[];
  motivation_profile: Record<string, number>;
  contribution_profile: Record<string, number>;
  report_data: ReportData;
  computed_at: string;
}

export type PartnerArchetype =
  | 'clinical_anchor'
  | 'operational_stabiliser'
  | 'strategic_builder'
  | 'people_culture_steward'
  | 'governance_guardian'
  | 'adaptive_integrator'
  | 'performance_driver'
  | 'consensus_builder'
  | 'change_catalyst'
  | 'quality_champion'
  | 'team_connector'
  | 'systems_thinker'
  | 'risk_navigator'
  | 'innovation_advocate'
  | 'balance_keeper'
  | 'steady_anchor';

export interface ArchetypeDefinition {
  id: string;
  archetype: PartnerArchetype;
  name: string;
  description: string;
  strengths: string[];
  challenges: string[];
  best_conditions: string[];
  domain_pattern: Record<string, string>;
}

export interface ReportData {
  leadershipStyle?: string;
  motivationDrivers?: string;
  contributionOrientation?: string;
  fairnessPreferences?: string;
  partnershipStrengths?: string[];
  frictionRisks?: string[];
  pressureBehaviours?: string;
  bestConditions?: string[];
  developmentActions?: string[];
}

export const SECTION_NAMES: Record<InventorySection, string> = {
  leadership_behaviour: 'Leadership Behaviour Preferences',
  leadership_pressure: 'Leadership Under Pressure',
  motivation_drivers: 'Motivation Drivers',
  values_assumptions: 'Values and Working Assumptions',
  contribution_orientation: 'Contribution and Role Orientation',
  decision_governance: 'Decision and Governance Preferences',
  fairness_working: 'Fairness and Ways of Working',
  team_climate: 'Team Climate and Psychological Safety',
};

export const LIKERT_OPTIONS = [
  { value: 1, label: 'Strongly disagree', color: 'bg-red-500' },
  { value: 2, label: 'Disagree', color: 'bg-orange-500' },
  { value: 3, label: 'Slightly disagree', color: 'bg-amber-500' },
  { value: 4, label: 'Neutral', color: 'bg-gray-400' },
  { value: 5, label: 'Slightly agree', color: 'bg-lime-500' },
  { value: 6, label: 'Agree', color: 'bg-green-500' },
  { value: 7, label: 'Strongly agree', color: 'bg-emerald-600' },
];
